// Adapter between the sim and the static data modules (src/data/*). Everything the sim needs from a
// definition goes through here so stat-key differences / missing values are handled in one place.
import { EQUIPMENT, itemPrice } from '../data/equipment.js';
import { FIELDS, enableM2Fields, enableM3Fields, enableM4Fields } from '../data/fields.js';
import { REGIONS, regionRisk as dataRegionRisk } from '../data/regions.js';
import { ROLES, marketWage as dataMarketWage } from '../data/wages.js';
import { inflate } from '../data/cpi.js';
import { dayToDate, yearFrac } from '../core/time.js';

export { EQUIPMENT, FIELDS, REGIONS, ROLES };

// M2 switched on (M2_SPEC §0): the Gusher & Rotary fields are ordinary FIELDS entries for the sim. Field generation
// uses a per-field RNG (core/state.js genField), so this does not perturb M1 seeds beyond the one-time re-baseline.
enableM2Fields();
// M3 ops: the Postwar & Offshore fields (M3_SPEC §0) — same per-field RNG, so M1/M2 seeds are unchanged.
// DRILLER_NO_M3=1 (Node only) keeps them hidden: tests use it to compare seeded games with and without M3 data.
if (!(typeof process !== 'undefined' && process.env?.DRILLER_NO_M3)) enableM3Fields();
// M4 (M4_SPEC §0): the 24 shale/deepwater fields and 6 regions; DRILLER_NO_M4=1 (Node only) keeps them hidden for invariance tests
if (!(typeof process !== 'undefined' && (process.env?.DRILLER_NO_M4 || process.env?.DRILLER_NO_M3))) enableM4Fields();

// Calendar lookups are hot (every field, every day): memoize the last day seen (pure functions of the day).
let _yDay = NaN, _yYear = 0, _yfDay = NaN, _yfVal = 0;
export const yearOf = (state) => { if (state.day !== _yDay) { _yDay = state.day; _yYear = dayToDate(state.day).year; } return _yYear; };
export const yf = (state) => { if (state.day !== _yfDay) { _yfDay = state.day; _yfVal = yearFrac(state.day); } return _yfVal; };

export function eqDef(id) { return EQUIPMENT[id] ?? null; }
export function fieldDef(id) { return FIELDS[id] ?? null; }
export function regionDef(id) { return (id && REGIONS[id]) || null; }

export const stat = (def, key, dflt) => (def && def.stats && def.stats[key] !== undefined ? def.stats[key] : dflt);
export const defYear = (def) => def?.year ?? 1859;

// Historical availability (year reached). Research unlocks are layered on top by research.isUnlocked().
export function isAvailable(def, state) {
  if (!def) return false;
  if (defYear(def) <= yearOf(state)) return true;
  return !!state?.research?.unlockedDefs?.[def.id];
}

// Era-dollar price of one unit (per-ft items: price per ft).
export function unitPrice(state, def) {
  if (!def) return 0;
  const y = Math.min(yf(state), 2025);
  return itemPrice(def, y);
}

// Era-dollar amount quoted in `baseYear` dollars.
export function eraDollars(state, amount, baseYear = 1859) {
  return inflate(amount, baseYear, Math.min(yf(state), 2025));
}

export function isStackable(def) {
  if (!def) return false;
  if (def.stackable !== undefined) return def.stackable;
  if (def.cat === 'line') return true;
  if (def.cat === 'pipe' || def.cat === 'mud') return true; // M2: drill pipe by the joint, mud by the foot
  if (def.cat === 'casing') return !stat(def, 'perJob', false);
  if (def.cat === 'storage') return !!stat(def, 'consumable', false);
  if (def.cat === 'stim') return true;
  return false;
}

const BY_CAT = {};
for (const d of Object.values(EQUIPMENT)) (BY_CAT[d.cat] ??= []).push(d);
export function defsByCat(cat) {
  return BY_CAT[cat] ?? [];
}

// Cheapest available def of a category matching a predicate (used to find starter items without hard-coding ids).
export function findDef(cat, pred = () => true, state = null) {
  const list = defsByCat(cat).filter(pred).filter((d) => !state || isAvailable(d, state));
  list.sort((a, b) => defYear(a) - defYear(b) || (a.basePrice ?? 0) - (b.basePrice ?? 0));
  return list[0] ?? null;
}

// ---- rigs / engines / bits ----
export function rigStats(def) {
  return {
    maxDepth: stat(def, 'maxDepth', 250),
    baseRop: stat(def, 'baseRop', 12),
    rigUpDays: stat(def, 'rigUpDays', 2),
    trip: stat(def, 'trip', 0),
    rigType: stat(def, 'rigType', 'cable'),
    requiredRoles: stat(def, 'requiredRoles', { driller: 1, laborer: 1 }),
    needsEngine: stat(def, 'needsEngine', false),
    powerMult: stat(def, 'selfPowerMult', stat(def, 'powerMult', null)), // manpower rigs carry their own
    fuelPerDay: stat(def, 'fuelPerDay', 0),
  };
}

export function bitStats(def) {
  return { speed: stat(def, 'speed', 1), life: stat(def, 'life', 300), maxHard: stat(def, 'maxHard', 6) };
}

export function bitFitsRig(bitDef, rigDef) {
  const rt = rigStats(rigDef).rigType;
  return !bitDef.rigTypes || bitDef.rigTypes.includes(rt);
}

export const MANPOWER_MULT = 0.6;

// ---- storage / transport ----
export function isBarrelDef(def) { return def?.cat === 'storage' && !!stat(def, 'consumable', false); }
let _barrel;
export function barrelDef() { return _barrel === undefined ? (_barrel = findDef('storage', isBarrelDef)) : _barrel; } // static data

export function transportStats(def) {
  let c = stat(def, 'costPerBbl', 1);
  if (Array.isArray(c)) c = (c[0] + c[1]) / 2;
  return {
    costPerBbl: c, costRange: stat(def, 'costRange', null),
    capacityPerDay: stat(def, 'capacityPerDay', 20),
    connectCost: stat(def, 'connectCost', 0),
    needsBarrels: !!stat(def, 'needsBarrels', false),
    mudSeasonMult: stat(def, 'mudSeasonMult', 1),
    requires: stat(def, 'requires', null),
    perTeamster: !!stat(def, 'perTeamster', def?.id === 'teamster'),
  };
}

// Field transport list entries are "id" or "id@year".
export function fieldTransports(fdef) {
  return (fdef?.transport ?? []).map((t) => {
    if (typeof t === 'object') return t;
    const [id, y] = String(t).split('@');
    return { id, year: y ? Number(y) : 0 };
  });
}

// ---- roles ----
export function roleExists(role) { return !!ROLES[role]; }
export function marketWage(role, year, skill) {
  if (!ROLES[role]) return 1.25 * (role === 'guard' ? 0.9 : 1) * (0.8 + 0.6 * skill / 100);
  return dataMarketWage(role, year, skill);
}

// ---- regions ----
// Returns { stability, security, expropriation, corruption } for region at fractional year.
// Memoized per region on the last year asked (hot: every site, every day). Callers treat the result as read-only.
const RISK_MEMO = new Map();
export function regionRisk(regionId, year) {
  if (!regionId) return { stability: 1, security: 1, expropriation: 0, corruption: 0 };
  const k = REGIONS[regionId]?.risk;
  let m = RISK_MEMO.get(regionId);
  if (!m || m.k !== k || m.byYear.size > 64) RISK_MEMO.set(regionId, (m = { k, byYear: new Map() }));
  let val = m.byYear.get(year);
  if (val === undefined) m.byYear.set(year, (val = regionRiskRaw(regionId, year)));
  return val;
}
function regionRiskRaw(regionId, year) {
  try {
    const r = dataRegionRisk(regionId, year);
    if (r) return r;
  } catch { /* fall through */ }
  const def = regionDef(regionId);
  const k = def?.risk;
  if (!k || !k.length) return { stability: 1, security: 1, expropriation: 0, corruption: 0 };
  if (year <= k[0].year) return k[0];
  for (let i = 1; i < k.length; i++) {
    if (year <= k[i].year) {
      const a = k[i - 1], b = k[i], t = (year - a.year) / (b.year - a.year);
      const o = {};
      for (const key of ['stability', 'security', 'expropriation', 'corruption']) o[key] = a[key] + (b[key] - a[key]) * t;
      return o;
    }
  }
  return k[k.length - 1];
}

export function fieldRegionId(fdef) { return fdef?.regionId ?? fdef?.region ?? null; }
